// libs
import React, { FunctionComponent } from 'react'

// interfaces / constants

// classes
import NavigationItem from 'src/components/navigation_item/navigation_item'
import IngredientRow from 'src/components/post/shared/ingredient_row/ingredient_row'

// styles
import { AddressFragmentFragment, EventFormat } from '@lopo-tech/lopo-api'
import { formatNeedsLink, formatNeedsPlace } from 'src/utils/event_format'
import { CLASS_PREFIX } from 'src/constants/'
import { textResources } from 'src/lang/de'
import { DisplaySettings, PostDisplayMode, Purpose } from 'src/interfaces/posts'
import './event_location.scss'

const clsBlock = CLASS_PREFIX + 'event-location'

type EventLocationSettings = {
  show: boolean
}

const _settings: DisplaySettings<EventLocationSettings> = {
  admin: {
    basic_ingredient: { show: true }
  },
  banner: {
    basic_ingredient: { show: true }
  },
  compact: {
    basic_ingredient: { show: false }
  },
  detail: {
    basic_ingredient: { show: true }
  },
  preview: {
    basic_ingredient: { show: true }
  }
}

export type IEventLocationProps = {
  // Optional since #155: an online event has no address at all. In the wizard
  // preview the field is simply absent - the server's fallback address only
  // exists once the item is saved - and destructuring it head-on took the whole
  // preview down with "Cannot read properties of undefined (reading 'city')".
  data?: AddressFragmentFragment;
  displayMode: PostDisplayMode;
  postDetailsURL?: string;
  purpose: Purpose;
  format?: EventFormat;
  onlineMeetingUrl?: string | null;
  // The map feature flag decides the address row alone. The link is not a map
  // feature, so the container answers this for the address and nothing else.
  showAddress?: boolean;
};

export const EventLocation: FunctionComponent<IEventLocationProps> = ({
  data,
  displayMode,
  postDetailsURL,
  purpose,
  format,
  onlineMeetingUrl,
  showAddress = true
}) => {
  const settings = _settings[displayMode][purpose]

  if (!settings?.show) {
    return null
  }

  const { city, description, zip, street } = data ?? {}
  const address = street ? `${street}, ${zip} ${city}` : description ?? ''
  // An event without an address still answers with one: the server falls back
  // to the author's location shape, so street, zip and city are all empty and
  // the row would render as a pin with nothing beside it.
  const withAddress = showAddress && formatNeedsPlace(format) && !!address
  // The scalar only stores http(s), measured against the API - but this row
  // renders a value other people wrote, and the check costs one comparison. A
  // value that does not carry a scheme we can link is shown as text.
  const linkable = !!onlineMeetingUrl && /^https?:\/\//i.test(onlineMeetingUrl)
  const withLink = formatNeedsLink(format) && !!onlineMeetingUrl

  if (!withAddress && !withLink) {
    return null
  }

  return (
    <>
      {withAddress && (
        <NavigationItem target="_self" url={postDetailsURL}>
          <IngredientRow
            icon="pin-standard"
            title={address}
            displayMode={displayMode}
          />
        </NavigationItem>
      )}
      {/*
        The row reads "Link zur Veranstaltung" rather than the address itself, as
        the app does - the URL is the destination, not the text. A bare URL as
        link text is also what a screen reader spells out character by character.

        It is a plain underlined link, not the action gradient: that was tried
        and turned down for being harder to read and louder than the row's place
        in a feed warrants.

        The globe keeps the default position. The pin next to it carries a
        `right: 1px` nudge because its glyph is narrow and sits off-centre in its
        box; the globe fills its box symmetrically, so the same shift would push
        it out of line rather than into it. Measured at 2x on a hybrid event: the
        icon boxes differ by 1px, and the text edge both rows are read along is
        identical.
      */}
      {withLink && (
        linkable
          ? (
            <a
              className={`${clsBlock}__link ${clsBlock}__link__${displayMode}`}
              href={onlineMeetingUrl as string}
              target="_blank"
              rel="noreferrer noopener"
            >
              <IngredientRow
                icon="world"
                title={textResources.eventfeed.eventUrl}
                displayMode={displayMode}
              />
            </a>
          )
          : (
            // Nothing to link to, so nothing that a label could stand for: the
            // value is shown as it is rather than dressed as a link - and it
            // does not get the link colour either. The checks make this
            // unreachable from our own forms; it is what an older or foreign
            // value would land in.
            <div className={`${clsBlock}__plain ${clsBlock}__plain__${displayMode}`}>
              <IngredientRow
                icon="world"
                title={onlineMeetingUrl as string}
                displayMode={displayMode}
              />
            </div>
          )
      )}
    </>
  )
}
